import { USERS_ACCESS_ANALYTICS } from '../constants/usersAccess'
import type {
  UsersAccessPendingInviteRowI,
  UsersAccessTechnicianRowI,
  UsersAccessUserRowI,
} from '../types/usersAccess'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'

import {
  ManagedAccountsDocument,
  PendingInvitesDocument,
  TechniciansDocument,
  useDeleteTechnicianMutation,
  useOffboardUserMutation,
  useResendUserInviteMutation,
  useUpdateUserMutation,
  useUpdateUserRolesMutation,
} from '@/graphql/generated/hooks'
import { trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

interface EditUserValuesI {
  email: string
  firstName: string
  lastName: string
}

export const useUsersAccessMutations = (colorScheme: ColorScheme) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useToasts({
    colorScheme,
    isRebrand: true,
  })
  const [resendUserInvite, { loading: isResending }] =
    useResendUserInviteMutation()
  const [offboardUser, { loading: isOffboarding }] = useOffboardUserMutation()
  const [deleteTechnician, { loading: isRemovingTechnician }] =
    useDeleteTechnicianMutation()
  const [updateUserRoles, { loading: isUpdatingRoles }] =
    useUpdateUserRolesMutation()
  const [updateUser, { loading: isUpdatingUser }] = useUpdateUserMutation()

  const captureFailure = (action: string, error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        action,
        feature: 'managed_accounts',
        surface: 'settings_users_access',
      },
    })
    showError(t('settings:rebrand_users_action_error'))
  }

  // Pending-invite actions intentionally reuse the established legacy copy
  // because their outcomes are identical on the legacy and rebrand surfaces.
  const resendInvite = async (row: UsersAccessPendingInviteRowI) => {
    try {
      await resendUserInvite({
        variables: { input: { id: row.id } },
        refetchQueries: [{ query: PendingInvitesDocument }],
      })
      showSuccess(
        tVar(t, 'settings:pending_invitees_resend_success', {
          email: row.email,
        })
      )
      return true
    } catch (error) {
      captureFailure('resend_invite', error)
      return false
    }
  }

  const cancelInvitation = async (row: UsersAccessPendingInviteRowI) => {
    try {
      await offboardUser({
        variables: { input: { id: row.id } },
        refetchQueries: [
          { query: ManagedAccountsDocument },
          { query: PendingInvitesDocument },
        ],
      })
      showSuccess(t('settings:pending_invitees_cancel_success'))
      return true
    } catch (error) {
      captureFailure('cancel_invitation', error)
      return false
    }
  }

  const offboardAccount = async (row: UsersAccessUserRowI) => {
    try {
      await offboardUser({
        variables: { input: { id: row.id } },
        refetchQueries: [
          { query: ManagedAccountsDocument },
          { query: PendingInvitesDocument },
        ],
      })
      trackEvent(USERS_ACCESS_ANALYTICS.offboardSuccess, {
        roles: row.roles,
      })
      showSuccess(
        tVar(t, 'settings:rebrand_users_offboard_success', {
          name: row.fullName,
        })
      )
      return true
    } catch (error) {
      trackEvent(USERS_ACCESS_ANALYTICS.offboardFailure, { roles: row.roles })
      captureFailure('offboard_user', error)
      return false
    }
  }

  const removeTechnician = async (row: UsersAccessTechnicianRowI) => {
    try {
      await deleteTechnician({
        variables: { input: { id: row.id } },
        refetchQueries: [{ query: TechniciansDocument }],
      })
      showSuccess(t('settings:rebrand_users_remove_technician_success'))
      return true
    } catch (error) {
      captureFailure('remove_technician', error)
      return false
    }
  }

  const changeRoles = async (row: UsersAccessUserRowI, roles: string[]) => {
    try {
      await updateUserRoles({
        variables: { input: { id: row.id, roles } },
        refetchQueries: [{ query: ManagedAccountsDocument }],
      })
      showSuccess(
        tVar(t, 'settings:rebrand_users_change_roles_success', {
          name: row.fullName,
        })
      )
      return true
    } catch (error) {
      captureFailure('change_roles', error)
      return false
    }
  }

  const editUser = async (
    row: UsersAccessUserRowI,
    values: EditUserValuesI
  ) => {
    try {
      await updateUser({
        variables: {
          input: {
            id: row.id,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            roleName: row.roleName ?? '',
          },
        },
        refetchQueries: [{ query: ManagedAccountsDocument }],
      })
      showSuccess(t('settings:rebrand_users_edit_success'))
      return true
    } catch (error) {
      captureFailure('edit_user', error)
      return false
    }
  }

  return {
    cancelInvitation,
    changeRoles,
    editUser,
    isOffboarding,
    isRemovingTechnician,
    isResending,
    isUpdatingRoles,
    isUpdatingUser,
    offboardAccount,
    removeTechnician,
    resendInvite,
  }
}
